// CompleteAccount — one-time step for Google users to create a LearnixTa password.
// Name prefilled from Google (editable), email read-only, password + confirm.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, Eye, EyeOff, User, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { useAuth, formatApiErrorDetail } from '../lib/auth';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function CompleteAccount() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === false) { navigate('/signin', { replace: true }); return; }
    if (user && user.name && !name) setName(user.name);
    if (user && user.has_password) {
      // Password already set — this page is one-time only.
      const dest = user.primary_path || user.onboarding_complete ? '/dashboard' : '/signup';
      navigate(dest, { replace: true });
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!PASSWORD_RULE.test(password)) {
      setError('Password must be at least 8 characters with at least one letter and one number.');
      return;
    }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-password`, { password, name: name.trim() || undefined });
      await refresh();
      const hasLexi = !!localStorage.getItem('lexi_session');
      const onboarded = user && (user.primary_path || user.onboarding_complete);
      navigate(onboarded ? '/dashboard' : hasLexi ? '/signup' : '/signup', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50">
      <Navbar />
      <div className="max-w-md mx-auto px-4 pt-24 pb-16">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8" data-testid="complete-account-card">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Complete Your LearnixTa Account</h1>
          <p className="text-sm text-gray-500 mb-6">Your email is verified by Google. Create a password so you can also sign in with email later.</p>
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="complete-account-error">{error}</div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="ca-name" className="text-gray-700">Full Name</Label>
              <div className="relative mt-1">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input id="ca-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="pl-9" maxLength={120} data-testid="complete-account-name" />
              </div>
            </div>
            <div>
              <Label htmlFor="ca-email" className="text-gray-700">Email (verified by Google)</Label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input id="ca-email" value={user.email || ''} readOnly disabled
                  className="pl-9 bg-gray-50 text-gray-500" data-testid="complete-account-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="ca-pw" className="text-gray-700">Create Password</Label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input id="ca-pw" type={showPw ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-10"
                  placeholder="Min 8 chars, letters + numbers" data-testid="complete-account-password" />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="complete-account-toggle-pw" aria-label="Show or hide password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="ca-pw2" className="text-gray-700">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input id="ca-pw2" type={showPw ? 'text' : 'password'} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className="pl-9"
                  data-testid="complete-account-confirm" />
              </div>
            </div>
            <Button type="submit" disabled={busy || !password || !confirm}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="complete-account-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
